<template>
  <a
    v-city-tour-view-tracker="{}"
    class="tour-card"
    target="_blank"
    :href="link"
    @click.self="handleSnippetClick('snippet')"
  >
    <ui-image
      v-if="coverImg"
      class="tour__cover"
      :photo="coverImg"
      :alt="exp.title || ''"
      fit="cover"
    />
    <div
      class="tour-content"
      @click.self="handleSnippetClick('snippet')"
    >
      <header
        v-if="hasNearestDates"
        class="tour__dates"
        @click="handleSnippetClick('date')"
      >
        {{ t(props.closestTourLabelKey) }}
        <span class="tour__dates-date">
          {{ nearestDatesString }}
        </span>
      </header>
      <h3
        class="tour__title"
        @click="handleSnippetClick('name')"
      >
        {{ exp?.title }}
      </h3>
      <div
        class="tour__details"
        @click="handleSnippetClick('price')"
      >
        {{ duration }} ·
        <CityTourPrice :price="props.exp?.price" />
      </div>
      <a
        v-if="exp.guide"
        class="tour__guide"
        :href="guideLink"
      >
        <ui-image
          v-if="exp.guide.avatar"
          v-city-tour-guide-click-tracker="{}"
          class="tour__guide-ava"
          :src="exp.guide.avatar.small"
          :alt="t('city_tour.organizer')"
          avatar-size="small"
        />
        <span
          v-city-tour-guide-click-tracker="{}"
          class="tour__guide-info"
        >
          {{ exp.guide.first_name }}
          <span
            v-city-tour-guide-click-tracker="{}"
            class="tour__guide-role"
          >
            {{ t('city_tour.organizer') }}</span
          >
        </span>
      </a>
    </div>
  </a>
</template>

<script setup lang="ts">
  import { UiImage } from '@experience/core-image-ui';
  import type { Experience } from '@/shared/api/experience';
  import { durationDates, durationInDays } from '@experience/datetime-utils';
  import CityTourPrice from '@/entities/cityTour/ui/CityTourPrice.vue';
  import { useI18n } from '@experience/i18n';
  import ru from '../i18n/ru.json';
  import { withoutDomain } from '@experience/shared';
  import { trackEvent, useClickTracker, useViewTracker } from '@/entities/sogu';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  interface Props {
    exp: Experience;
    defaultSoguFields?: ContextGeoSubject;
    closestTourLabelKey?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    closestTourLabelKey: 'city_tour.closest_tour',
  });
  const hasNearestDates = computed(() => props.exp.schedule?.upcoming_events?.length);
  const coverImg = computed(() => props.exp.photos?.[0]);
  const nearestDatesString = computed(() => {
    const { duration, schedule } = props.exp;
    const nearestStart = schedule?.upcoming_events ? schedule?.upcoming_events[0] : '';

    return durationDates(nearestStart, duration || 0);
  });

  const duration = computed(() => (props.exp?.duration ? durationInDays(props.exp?.duration) : 0));

  const guideLink = computed(() => withoutDomain(props.exp?.guide?.url || ''));
  const link = computed(() => withoutDomain(props.exp?.url || ''));

  const soguAdapteredData = computed(() => ({
    subject_geo_type: props.defaultSoguFields?.soguStamp.subject_geo_type,
    city_id: props.exp.geo?.city?.length ? props.exp.geo?.city[0]?.id : 0,
    city_name: props.exp.geo?.city?.length ? props.exp.geo?.city[0]?.name : '',
    experience_id: props.exp.id,
    guide_id: props.exp.guide?.id,
    title: props.exp.title,
    format: props.exp.format,
    type: props.exp.type,
    full_price: props.exp.price.value,
    instant_booking: props.exp.is_instant_booking,
    discount_rate: props.exp.price.discount?.value || 0,
    duration: props.exp.duration,
    rating: props.exp.rating,
  }));

  const vCityTourViewTracker = useViewTracker('city-listing:tours:experience.view', { ...soguAdapteredData.value });

  const vCityTourGuideClickTracker = useClickTracker('city-listing:tours:experience:guide.click', {
    ...soguAdapteredData.value,
  });

  const handleSnippetClick = (clickType: string) => {
    trackEvent('city-listing:tours:experience.click', { ...soguAdapteredData.value, click_type: clickType });
  };
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .tour-card {
    display: grid;
    border-radius: 8px;
    color: var(--alias-color-text-inverse);
    text-decoration: none;
    cursor: pointer;
    transition: transform 0.1s linear;

    @include adaptivity.tablet {
      &:hover {
        transform: translateY(-8px);
      }
    }
  }

  .tour__cover {
    display: block;
    grid-area: -1/-1;
    object-fit: cover;
    width: 100%;
    height: 100%;
    border-radius: inherit;
  }

  .tour-content {
    display: flex;
    flex-direction: column;
    grid-area: -1/-1;
    padding: 16px;
    border-radius: inherit;
    background: var(--global-color-transparent-black-a500);
  }

  .tour__dates {
    color: var(--global-color-transparent-white-a700);

    @include typography.base-200-200-medium;
  }

  .tour__dates-date {
    display: block;
    color: var(--alias-color-text-inverse);

    @include typography.base-300-300-regular;
  }

  .tour__title {
    margin: 48px 0 0;

    @include typography.base-700-500-extra;
  }

  .tour__details {
    margin: 12px 0;

    @include typography.base-300-300-regular;
    @include typography.base-300-300-regular;

    :deep(.price) {
      display: inline;
      color: var(--alias-color-text-inverse);
    }

    :deep(.price-current) {
      color: var(--alias-color-text-inverse);

      &.price-discount {
        color: var(--alias-color-text-inverse);
      }
    }
  }

  .tour__guide {
    display: flex;
    align-items: flex-end;
    margin-top: auto;
    color: var(--alias-color-text-inverse);
    text-decoration: none;

    @include typography.base-300-300-regular;
  }

  .tour__guide-ava {
    width: 36px;
    height: 36px;
    border-radius: 50%;
  }

  .tour__guide-info {
    display: flex;
    flex-direction: column;
    margin: 0 0 0 16px;
  }

  .tour__guide-role {
    color: var(--global-color-transparent-white-a700);

    @include typography.base-200-200-medium;
  }
</style>

<style lang="scss"></style>
